import { useState } from "react";
import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";
import { formatDateTimeShort } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatFileSize } from "@/hooks/use-project-documents";
import type { UserLookup } from "@/hooks/use-users";
import {
  useCreateComment,
  useDeleteComment,
  useToggleCommentLike,
  type ProjectComment,
} from "@/hooks/use-project-comments";
import { usePermissions } from "@/hooks/use-permissions";
import { HTML_SANITIZE_OPTS, renderMentions } from "@/lib/comment-sanitize";
import { useMentionComposer } from "@/hooks/use-mention-composer";
import { MentionListbox } from "./MentionListbox";
import { CommentLikeButton } from "./CommentLikeButton";

interface Props {
  comment: ProjectComment;
  projectId: string;
  authUserId: string | undefined;
  /** Brukes til å rendre `@Navn`-mentions med riktig styling. */
  users?: UserLookup[];
  isReply?: boolean;
}

function isImageExtension(ext: string | null): boolean {
  if (!ext) return false;
  return ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext.toLowerCase());
}

/**
 * Rendrer én kommentar (top-level eller reply). Body rendres som sanitert
 * HTML hvis tilgjengelig (Monday-import), ellers som plain tekst med
 * @-mention-styling. Attachments listes som klikkbare lenker (bilder vises
 * inline). Like- og svar-knapp lar brukerne interagere fra Sølvrev (samme
 * underliggende tabeller som Monday-importen bruker).
 *
 * Replies rendres rekursivt indented under parent.
 */
export function CommentMessage({
  comment: c,
  projectId,
  authUserId,
  users = [],
  isReply = false,
}: Props) {
  const likeMut = useToggleCommentLike();
  const replyMut = useCreateComment();
  const deleteMut = useDeleteComment();
  const { isAdmin } = usePermissions();
  const [replyOpen, setReplyOpen] = useState(false);
  // Samme @-tagging som hovedfeltet, med egen id-prefiks per kommentar så
  // flere åpne svarfelt ikke deler listbox-id.
  const reply = useMentionComposer({ users, idPrefix: `mention-reply-${c.id}` });
  const replyBody = reply.body;

  const isMe = authUserId === c.author_id;
  const canDelete = isMe || isAdmin;

  const handleDelete = () => {
    if (deleteMut.isPending) return;
    if (!confirm("Slett denne meldingen?")) return;
    deleteMut.mutate({ id: c.id, project_id: projectId });
  };
  const initials = (c.author?.full_name ?? "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2);

  const renderedHtml = c.body_html
    ? DOMPurify.sanitize(c.body_html, HTML_SANITIZE_OPTS)
    : renderMentions(c.body, c.mentioned_user_ids ?? [], users, isMe && !isReply);

  const userHasLiked = !!authUserId && (c.liked_by_user_ids ?? []).includes(authUserId);
  const likeCount = c.like_count ?? (c.liked_by_user_ids?.length ?? 0);

  const handleLike = () => {
    if (!authUserId || likeMut.isPending) return;
    likeMut.mutate({
      comment_id: c.id,
      user_id: authUserId,
      project_id: projectId,
      currently_liked: userHasLiked,
    });
  };

  const handleSendReply = () => {
    if (!replyBody.trim() || !authUserId) return;
    // Replies kobles til root-parent for å holde tråder flate (Monday gjør
    // det samme — reply-on-reply havner som søsken-reply).
    const parentId = c.parent_comment_id ?? c.id;
    replyMut.mutate(
      {
        project_id: projectId,
        author_id: authUserId,
        body: replyBody.trim(),
        mentioned_user_ids: reply.resolveMentionedIds(),
        parent_comment_id: parentId,
      },
      {
        onSuccess: () => {
          reply.reset();
          setReplyOpen(false);
        },
      },
    );
  };

  return (
    <div className={cn("flex gap-3", isMe && !isReply && "flex-row-reverse")}>
      {c.author?.avatar_url ? (
        <img
          src={c.author.avatar_url}
          alt=""
          className={cn(
            "rounded-full object-cover flex-shrink-0",
            isReply ? "w-6 h-6" : "w-8 h-8",
          )}
        />
      ) : (
        <span
          className={cn(
            "rounded-full bg-primary/10 text-primary font-medium flex items-center justify-center flex-shrink-0",
            isReply ? "w-6 h-6 text-[10px]" : "w-8 h-8 text-xs",
          )}
        >
          {initials}
        </span>
      )}

      <div className={cn("max-w-[70%]", isMe && !isReply ? "text-right" : "")}>
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-medium">{c.author?.full_name ?? "Ukjent"}</span>
          <span className="text-[10px] text-muted-foreground">
            {formatDateTimeShort(c.created_at)}
          </span>
        </div>

        {(c.body_html || c.body) && (
          <div
            className={cn(
              // Bevisst UTEN `prose`: klassene sto her, men typografi-pluginen
              // var aldri registrert (VIB-2799), så de har aldri gjort noe.
              // Nå som den er registrert ville de endret kommentarboblene —
              // `prose` setter sin egen tekstfarge og ville overstyrt
              // `text-primary-foreground` på egne meldinger, så den lilla boblen
              // fikk mørk tekst. Boblene beholder utseendet de har i dag.
              "rounded-lg px-3 py-2 text-sm inline-block text-left",
              isMe && !isReply ? "bg-primary text-primary-foreground" : "bg-muted",
            )}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        )}

        {c.attachments && c.attachments.length > 0 && (
          <div className="mt-1 space-y-1">
            {c.attachments.map((att) => (
              <a
                key={att.id}
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "rounded-lg px-3 py-2 text-xs inline-flex items-center gap-2 max-w-full",
                  isMe && !isReply ? "bg-primary text-primary-foreground" : "bg-muted",
                )}
              >
                {isImageExtension(att.file_extension) ? (
                  <img
                    src={att.url}
                    alt={att.name}
                    className="max-w-[200px] max-h-[150px] rounded object-cover"
                  />
                ) : (
                  <>
                    <SrIcon name="file-text" className="w-4 h-4 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{att.name}</div>
                      {att.file_size != null && (
                        <div className="opacity-75">{formatFileSize(att.file_size)}</div>
                      )}
                    </div>
                    <SrIcon name="download" className="w-3 h-3 flex-shrink-0 opacity-50" />
                  </>
                )}
              </a>
            ))}
          </div>
        )}

        {/* Action-rad: like + svar */}
        <div
          className={cn(
            "mt-1 flex items-center gap-2 text-[11px] text-muted-foreground",
            isMe && !isReply && "justify-end",
          )}
        >
          <CommentLikeButton
            likedByUserIds={c.liked_by_user_ids ?? []}
            likeCount={likeCount}
            userHasLiked={userHasLiked}
            users={users}
            authUserId={authUserId}
            disabled={!authUserId || likeMut.isPending}
            onToggle={handleLike}
          />
          <span aria-hidden>·</span>
          <button
            type="button"
            onClick={() => setReplyOpen((v) => !v)}
            disabled={!authUserId}
            className="hover:text-primary disabled:opacity-50"
          >
            {replyOpen ? "Avbryt" : "Svar"}
          </button>
          {canDelete && (
            <>
              <span aria-hidden>·</span>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMut.isPending}
                className="hover:text-destructive disabled:opacity-50"
                title="Slett melding"
              >
                Slett
              </button>
            </>
          )}
        </div>

        {replyOpen && (
          <div className="relative mt-2 flex gap-2">
            <MentionListbox composer={reply} />
            <textarea
              autoFocus
              aria-autocomplete="list"
              aria-controls={reply.mentionsOpen ? reply.listboxId : undefined}
              aria-activedescendant={reply.highlightedUser ? reply.optionId(reply.highlightedUser.id) : undefined}
              className="flex-1 min-h-[36px] max-h-[120px] rounded-md border border-input bg-background px-2 py-1.5 text-xs resize-none"
              placeholder={`Svar til ${c.author?.full_name ?? "denne"}... Bruk @ for å tagge`}
              value={replyBody}
              onChange={(e) => reply.setBody(e.target.value)}
              onKeyDown={(e) => {
                // Åpen @-liste eier Enter/Escape; ellers sender Enter og Escape lukker svaret.
                if (reply.handleKeyDown(e)) return;
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
                if (e.key === "Escape") {
                  setReplyOpen(false);
                  reply.reset();
                }
              }}
              rows={1}
            />
            <button
              type="button"
              onClick={handleSendReply}
              disabled={!replyBody.trim() || replyMut.isPending}
              className="inline-flex items-center justify-center px-2 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-50"
            >
              {replyMut.isPending ? (
                <SrIcon name="loader2" className="w-3 h-3 animate-spin" />
              ) : (
                <SrIcon name="send" className="w-3 h-3" />
              )}
            </button>
          </div>
        )}

        {c.replies && c.replies.length > 0 && (
          <div className="mt-2 ml-2 pl-3 border-l-2 border-foreground/20 space-y-3">
            {c.replies.map((r) => (
              <CommentMessage
                key={r.id}
                comment={r}
                projectId={projectId}
                authUserId={authUserId}
                users={users}
                isReply
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
